import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, GraduationCap, Clock, Megaphone, Newspaper, AlertTriangle, ArrowRight, MapPin, Building2 } from 'lucide-react';
import { getKindMeta, getStatusMeta } from '../../utils/announcementStatus';
import Countdown from './Countdown';
import NotifyMeForm from './NotifyMeForm';
import './AnnouncementCard.css';

const KIND_ICON = {
  intake: Calendar,
  scholarship: GraduationCap,
  deadline: Clock,
  event: Megaphone,
  news: Newspaper,
  alert: AlertTriangle,
};

/** One card, reused by HomeAnnouncements, the /announcements hub, and the
 * related-announcements list on AnnouncementDetail. `headingLevel` defaults
 * to h3 (correct when a card grid sits under an h2 section heading, as on
 * HomeAnnouncements and the detail page's related list) — the hub page has
 * no such h2 between its h1 and the grid, so it passes "h2" instead to
 * avoid skipping a heading level. */
export default function AnnouncementCard({ announcement, headingLevel = 'h3' }) {
  const kindMeta = getKindMeta(announcement.kind);
  const statusMeta = getStatusMeta(announcement.status);
  const Icon = KIND_ICON[announcement.kind] || Newspaper;
  const isExternal = /^https?:\/\//i.test(announcement.cta_url || '');
  const Heading = headingLevel;

  return (
    <article className="ann-card">
      <div className="ann-card-top">
        <span className={`ann-badge ve-tone-${kindMeta.tone}`}>
          <Icon size={14} aria-hidden="true" /> {kindMeta.label}
        </span>
        {statusMeta && (
          <span className={`ann-badge ve-tone-${statusMeta.tone}`}>{statusMeta.label}</span>
        )}
      </div>

      <Heading className="ann-card-title">
        <Link to={`/announcements/${announcement.slug}`}>{announcement.title}</Link>
      </Heading>

      <p className="ann-card-summary">{announcement.summary}</p>

      {(announcement.university_name || announcement.country_name) && (
        <div className="ann-card-chips">
          {announcement.university_name && (
            <span className="ann-chip"><Building2 size={12} aria-hidden="true" /> {announcement.university_name}</span>
          )}
          {announcement.country_name && (
            <span className="ann-chip"><MapPin size={12} aria-hidden="true" /> {announcement.country_name}</span>
          )}
        </div>
      )}

      <div className="ann-card-footer">
        {announcement.closes_at && announcement.status !== 'closed' && (
          <Countdown closesAt={announcement.closes_at} className="ann-card-countdown" />
        )}
        {announcement.cta_url && (
          isExternal ? (
            <a href={announcement.cta_url} target="_blank" rel="noopener noreferrer" className="ann-card-cta">
              {announcement.cta_label || 'Learn more'} <ArrowRight size={14} aria-hidden="true" />
            </a>
          ) : (
            <Link to={announcement.cta_url} className="ann-card-cta">
              {announcement.cta_label || 'Learn more'} <ArrowRight size={14} aria-hidden="true" />
            </Link>
          )
        )}
      </div>

      <NotifyMeForm announcement={announcement} />
    </article>
  );
}
